import type { FC } from 'react';

import { useCms, useContent } from '@eneco/cms';
import logger from '@eneco/common/log';
import RichTextComponent from '@eneco/components/RichText/RichText';

import { RICH_TEXT_BODY_FIELDS, type RichTextContent } from './rich-text-content.types';

/**
 * `RichText` block for the CMS-agnostic lane, serving **both** providers.
 *
 * The body is resolved by trying `RICH_TEXT_BODY_FIELDS` in order instead of assuming one field name:
 * Sitecore's unwrapped field is `content`, the POC schema uses `body`. `adapter.toRichText` then handles
 * the value shape — HTML string passthrough on Sitecore, Portable Text → HTML on Sanity.
 *
 * G2 (upstream, `@migrator/transform`): the CMS schema's `RichText` type is field-less, so a
 * *migrated* block genuinely has no body and this renders nothing. That is logged rather than shown as
 * an empty div, because on a parity run "blank on the Sanity lane, filled on the Sitecore lane" is the
 * finding — silence would hide it.
 */
const RichText: FC = () => {
  const { adapter } = useCms();
  const content = useContent<RichTextContent>();

  const field = RICH_TEXT_BODY_FIELDS.find(name => content[name] !== undefined && content[name] !== null);
  const html = field ? adapter.toRichText(content[field]) : undefined;

  if (!html) {
    logger.warn('cMsRt1', 'RichText block has no renderable body', {
      // Names both sides of the gap: which field carried a value (if any) and what the block did carry.
      field: field ?? 'none',
      tried: RICH_TEXT_BODY_FIELDS.join(', '),
      available: Object.keys(content).join(', '),
    });
    return null;
  }

  return (
    <div>
      <RichTextComponent html={html} />
    </div>
  );
};

export default RichText;
